/**
 * 🎁 Referral Management Page
 * Admin page for managing referrals and settings
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  CardContent,
  TableContainer,
  InputLabel,
  FormControl,
  Paper,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Settings as SettingsIcon, Refresh, CardGiftcard, CheckCircle, Schedule, MonetizationOn, Download, DateRange } from '@mui/icons-material';
import { adminReferralService, type AdminReferral, type ReferralSettings, type ReferralSummary } from '@/services/adminReferralService';
import toast from 'react-hot-toast';

export default function ReferralsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [summary, setSummary] = useState<ReferralSummary>({
    totalReferrals: 0,
    activeReferrals: 0,
    completedReferrals: 0,
    creditedReferrals: 0,
    pendingReferrals: 0,
    totalRewardsPaid: 0,
    totalPendingRewards: 0,
  });

  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settings, setSettings] = useState<ReferralSettings>({
    referrerReward: 500,
    refereeReward: 500,
    minBookingsForCompletion: 1,
    rewardExpiryDays: 90,
  });

  useEffect(() => {
    loadReferrals();
    loadSettings();
  }, [page, limit, statusFilter, searchQuery, startDate, endDate]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadReferrals();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [page, limit, statusFilter, searchQuery, startDate, endDate]);

  const loadReferrals = async () => {
    setLoading(true);
    try {
      const response = await adminReferralService.getReferrals({
        page,
        limit,
        status: statusFilter === 'all' ? undefined : statusFilter as any,
        search: searchQuery,
      });

      if (response.success && response.data) {
        setReferrals(response.data.referrals);

        // Update pagination data
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          setTotalPages(1);
          setTotalCount(response.data.referrals.length);
        }

        // New API returns 'summary'
        setSummary(response.data.summary || response.data.stats);
      } else {
        // Set empty data on error to prevent undefined errors
        setReferrals([]);
        setTotalPages(1);
        setTotalCount(0);
        console.error('API error:', response.error);
        // Don't show toast if it's a 500 error (backend not ready)
        if (!response.error?.includes('500') && !response.error?.includes('Internal server error')) {
          toast.error(response.error || 'Failed to load referrals');
        }
      }
    } catch (error) {
      console.error('Failed to load referrals:', error);
      setReferrals([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await adminReferralService.getSettings();

      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleUpdateSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await adminReferralService.updateSettings(settings);

      if (response.success) {
        toast.success('Settings updated successfully!');
        setSettingsOpen(false);
        await loadReferrals(); // Refresh to see updated rewards
      } else {
        toast.error(response.error || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (referrals.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV header
    const headers = ['Referrer', 'Referrer Code', 'Referred User', 'Code Used', 'Signup Date', 'Status', 'First Booking', 'Reward Amount', 'Reward Status', 'Credited At'];

    // Create CSV rows
    const rows = referrals.map(r => [
      r.referrer.fullName,
      r.referrer.code,
      r.referred.fullName,
      r.codeUsed,
      new Date(r.signupDate).toLocaleDateString('en-NG'),
      getStatusLabel(r.status),
      r.firstBooking ? 'Yes' : 'No',
      `₦${r.reward.amount.toLocaleString()}`,
      r.reward.status === 'pending' ? 'Pending' : 'Credited',
      r.reward.creditedAt ? new Date(r.reward.creditedAt).toLocaleDateString('en-NG') : '-'
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `referrals-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success(`Exported ${referrals.length} referrals to CSV`);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'active':
        return 'warning';
      case 'completed':
        return 'info';
      case 'credited':
        return 'success';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Awaiting First Booking';
      case 'completed':
        return 'First Booking Complete';
      case 'credited':
        return 'Rewards Credited';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {title.includes('Paid') ? `₦${value.toLocaleString()}` : value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const SettingsTabContent = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CardGiftcard sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Referral Program Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current settings for the referral rewards program
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Referrer Reward
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  ₦{settings.referrerReward.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reward for the person sharing their referral code
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Referee Reward
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  ₦{settings.refereeReward.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reward for the new user using a referral code
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Minimum Bookings Required
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {settings.minBookingsForCompletion}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bookings needed before rewards are credited
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Reward Expiry
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                  {settings.rewardExpiryDays === 0 ? 'Never' : `${settings.rewardExpiryDays} days`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings.rewardExpiryDays === 0 ? 'Rewards never expire' : 'Days until unclaimed rewards expire'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {settings.updatedAt && (
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date(settings.updatedAt).toLocaleString('en-NG', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={async () => {
                await loadSettings();
                setSettingsOpen(true);
              }}
              size="large"
            >
              Edit Settings
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Referral Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage referral program settings and track all referrals
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {activeTab === 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<DateRange />}
                onClick={() => setDateFilterOpen(!dateFilterOpen)}
              >
                Date Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportCSV}
                disabled={referrals.length === 0}
              >
                Export CSV
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              loadReferrals();
              loadSettings();
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={async () => {
              await loadSettings();
              if (activeTab === 1) {
                // Already on settings tab, just open dialog
                setSettingsOpen(true);
              } else {
                // Switch to settings tab
                setActiveTab(1);
              }
            }}
          >
            {activeTab === 1 ? 'Edit Settings' : 'Settings'}
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Referrals" />
          <Tab label="Program Settings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Referrals"
            value={summary?.totalReferrals || 0}
            icon={<CardGiftcard sx={{ color: 'white' }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Referrals"
            value={summary?.activeReferrals || 0}
            icon={<Schedule sx={{ color: 'white' }} />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Referrals"
            value={summary?.completedReferrals || 0}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Credited Referrals"
            value={summary?.creditedReferrals || 0}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Rewards Paid"
            value={summary?.totalRewardsPaid || 0}
            icon={<MonetizationOn sx={{ color: 'white' }} />}
            color="success.dark"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Rewards"
            value={summary?.totalPendingRewards || 0}
            icon={<MonetizationOn sx={{ color: 'white' }} />}
            color="warning.dark"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Search by referrer or referee name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1); // Reset to first page on search
              }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1); // Reset to first page on filter
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active (Awaiting First Booking)</MenuItem>
                <MenuItem value="completed">Completed (First Booking Done)</MenuItem>
                <MenuItem value="credited">Credited (Rewards Paid)</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Per Page</InputLabel>
              <Select
                value={limit}
                label="Per Page"
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1); // Reset to first page when changing limit
                }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Typography variant="body2" color="text.secondary" align="right" sx={{ lineHeight: '40px' }}>
              {totalCount > 0 ? (
                <>
                  {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)} of {totalCount}
                </>
              ) : (
                '0 referrals'
              )}
            </Typography>
          </Grid>
        </Grid>

        {/* Date Range Filter */}
        {dateFilterOpen && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setPage(1);
                  }}
                  disabled={!startDate && !endDate}
                >
                  Clear Dates
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Card>

      {/* Referrals Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Referrer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Referred User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Signup Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>First Booking</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reward Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reward Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : referrals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CardGiftcard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No referrals found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statusFilter === 'all'
                        ? 'No referrals have been made yet'
                        : `No ${statusFilter} referrals at the moment`}
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> If the backend referral endpoints haven't been deployed yet, you'll see this empty state.
                      </Typography>
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                referrals.map((referral) => (
                  <TableRow key={referral.id} hover>
                    {/* Referrer */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {referral.referrer.code} ({referral.referrer.fullName})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {referral.referrer.email}
                      </Typography>
                    </TableCell>

                    {/* Code */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'action.hover',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          fontWeight: 600,
                        }}
                      >
                        {referral.codeUsed}
                      </Typography>
                    </TableCell>

                    {/* Referred User */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {referral.referred.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {referral.referred.email}
                      </Typography>
                    </TableCell>

                    {/* Signup Date */}
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(referral.signupDate).toLocaleDateString('en-NG', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={getStatusLabel(referral.status)}
                        color={getStatusColor(referral.status)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    {/* First Booking */}
                    <TableCell>
                      {referral.firstBooking ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            Yes
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(referral.firstBooking.created_at).toLocaleDateString('en-NG')}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No
                        </Typography>
                      )}
                    </TableCell>

                    {/* Reward Amount */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₦{referral.reward.amount.toLocaleString()}
                      </Typography>
                    </TableCell>

                    {/* Reward Status */}
                    <TableCell>
                      <Chip
                        label={referral.reward.status === 'pending' ? 'Pending' : 'Credited'}
                        color={referral.reward.status === 'pending' ? 'warning' : 'success'}
                        size="small"
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                      {referral.reward.creditedAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(referral.reward.creditedAt).toLocaleDateString('en-NG')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {page} of {totalPages} ({totalCount} total referrals)
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
            <Box sx={{ width: 200 }} /> {/* Spacer for centering */}
          </Box>
        )}
      </Card>
        </>
      ) : (
        <SettingsTabContent />
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Referral Program Settings
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Changes will apply to all new referrals. Existing pending referrals will use the old settings.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Referrer Reward (₦)"
              type="number"
              value={settings.referrerReward}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  referrerReward: parseFloat(e.target.value),
                })
              }
              helperText="Amount the referrer receives when referee completes first booking"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography>,
              }}
            />

            <TextField
              fullWidth
              label="Referee Reward (₦)"
              type="number"
              value={settings.refereeReward}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  refereeReward: parseFloat(e.target.value),
                })
              }
              helperText="Amount the new user receives when they complete first booking"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography>,
              }}
            />

            <TextField
              fullWidth
              label="Minimum Bookings for Completion"
              type="number"
              value={settings.minBookingsForCompletion}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  minBookingsForCompletion: parseInt(e.target.value),
                })
              }
              helperText="Number of completed bookings required before rewards are credited"
              inputProps={{ min: 1, step: 1 }}
            />

            <TextField
              fullWidth
              label="Reward Expiry (Days)"
              type="number"
              value={settings.rewardExpiryDays}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  rewardExpiryDays: parseInt(e.target.value),
                })
              }
              helperText="Days until unclaimed rewards expire (0 = never expire)"
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>

          {settings.updatedAt && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="caption">
                Last updated: {new Date(settings.updatedAt).toLocaleString('en-NG')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSettingsOpen(false)} disabled={settingsLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSettings}
            variant="contained"
            disabled={settingsLoading}
            startIcon={settingsLoading ? <CircularProgress size={20} /> : null}
          >
            {settingsLoading ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
